import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { SpacesRemainingTag } from 'Modules/FerrySchedule/Components/SpacesRemaining/SpacesRemainingTag';
import { isCapacityTagClosed } from 'Models/Shared/Capacity/isCapacityTagClosed';
import {
	OrderedCapacityItem,
	sortOrderedCapacities,
	splitPassengerClassTags,
} from 'Modules/FerrySchedule/Components/SpacesRemaining/capacityOrdering';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import { VehicleBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/VehicleBookingWizardData';
import { LegOptionContext } from './Contexts/LegOptionContext';
import LegOptionPaxCapacity from './LegOptionPaxCapacity';
import LegOptionCargoCapacity from './LegOptionCargoCapacity';
import LegOptionTrailerCapacity from './LegOptionTrailerCapacity';
import LegOptionFreightCapacity from './LegOptionFreightCapacity';

/**
 * Renders all of a trip option's capacities (static + dynamic capacity types) as one list ordered
 * by the configured display order (lower first). Statics take their order from app config; dynamic
 * capacity types use their own `order`. The static leaf components self-gate visibility (e.g. cargo
 * only renders on vehicle bookings), so non-applicable ones simply render nothing.
 */
/**
 * One capacity chip, with what the phone's collapsed row needs to know about it.
 */
interface CapacityChip {
	element: JSX.Element;
	/**
	 * Whether the chip draws anything on this tab. The static leaves gate themselves and render
	 * nothing where they do not apply, which is harmless in a full row but not in a counted one: a
	 * leaf that renders nothing must not take one of the visible places or be counted in `+N more`.
	 * Mirrors each leaf's own gate — keep the two together.
	 */
	applies: boolean;
	/** Space left, on a capacity the vessel actually has. Empty ones are tucked behind `+N more`. */
	hasSpace: boolean;
}

interface LegOptionCapacitiesProps {
	/**
	 * Show this many chips and fold the rest behind a `+N more` chip that opens them.
	 *
	 * <p>For the phone, where the chips share a line with the price and three named capacities is
	 * already three lines. The visible places go to capacities with space left; one with nothing
	 * left, or that the vessel does not carry, is still there behind `+N more` rather than dropped.
	 * Only folds when it saves at least two chips — `+1 more` takes the place of the one it hides.</p>
	 */
	collapseAfter?: number;
}

/** Space on a capacity the vessel has: something left, out of an allowance above zero. */
const hasSpaceLeft = (remaining: number | null | undefined, total: number | null | undefined) =>
	(remaining ?? 0) >= 1 && (total === null || total === undefined || total > 0);

export default function LegOptionCapacities({ collapseAfter }: LegOptionCapacitiesProps = {}) {
	const context = React.useContext(LegOptionContext);
	const intl = useIntl();

	if (!context) {
		console.error('LegOptionContext is missing in LegOptionCapacities');
		return null;
	}

	const {
		trip,
		wizardData,
		vehicleBookingsClosed,
		passengerBookingsClosed,
		freightBookingsClosed,
		showCapacityNames,
		explainClosedCapacity,
		explainCapacityState,
		cargoSpaces,
		paxSpaces,
	} = context;

	const tab = wizardData.tabSelected;

	// Classes are a breakdown of the passenger capacity, so they sit with it at the passenger order
	// in their own order, rather than each taking its own place among the other capacities.
	const { passengerClassTags, otherTags } = splitPassengerClassTags(trip.capacityTags);

	/*
		No vehicle fits, so nothing that rides on one fits either.

		Trailers, trucks and EV bays are a breakdown of the vehicle capacity, not alternatives to it:
		a sailing with no car space left cannot take a trailer however many trailer spots the vessel
		still has. Listing them was offering a reader four numbers of which only the first was real,
		and inviting the question "there are 7 truck spots, why can't I book?".

		The parent itself stays — which capacity ran out is the thing worth knowing.
	*/
	const vehiclesSoldOut = wizardData.tabSelected === 'vehicle' && (cargoSpaces ?? 0) < 1;

	const items: OrderedCapacityItem<CapacityChip>[] = [
		{
			order: appConfigStore.capacityOrderPassenger,
			isDynamic: false,
			hasOrder: true,
			item: {
				element: <LegOptionPaxCapacity key="pax" />,
				applies: !(tab === 'vehicle' && !appConfigStore.vehicleHasPassengersEnabled) && tab !== 'freight',
				hasSpace: hasSpaceLeft(paxSpaces, trip.passengerSpacesTotal),
			},
		},
		...passengerClassTags.map(passengerClassTag => ({
			order: appConfigStore.capacityOrderPassenger,
			// Not dynamic: a class must not win the dynamic-first tie-break against the passenger
			// capacity it belongs to.
			isDynamic: false,
			hasOrder: true,
			item: {
				applies: true,
				hasSpace: hasSpaceLeft(passengerClassTag.remaining, passengerClassTag.total),
				element: (
				<SpacesRemainingTag
					key={passengerClassTag.id}
					space={passengerClassTag.remaining}
					capacityName={passengerClassTag.name}
					icon={
						<Icon
							name={passengerClassTag.icon}
							useMaterialIcons={passengerClassTag.useMaterialIcon}
						/>
					}
					showCapacityRemaining={passengerClassTag.displayMode === 'ShowValue'}
					showName={showCapacityNames}
					explainClosed={explainClosedCapacity}
					explainState={explainCapacityState}
					tooltipText={showCapacityNames ? undefined : passengerClassTag.name}
					closed={isCapacityTagClosed(passengerClassTag, {
						vehicleBookingsClosed,
						passengerBookingsClosed,
						freightBookingsClosed,
					})}
				/>
				),
			},
		})),
		{
			order: appConfigStore.capacityOrderVehicle,
			isDynamic: false,
			hasOrder: true,
			item: {
				element: <LegOptionCargoCapacity key="cargo" />,
				applies: tab === 'vehicle',
				hasSpace: hasSpaceLeft(cargoSpaces, trip.vehicleSpacesTotal),
			},
		},
		...(vehiclesSoldOut
			? []
			: [
					{
						order: appConfigStore.capacityOrderTrailer,
						isDynamic: false,
						hasOrder: true,
						item: {
							element: <LegOptionTrailerCapacity key="trailer" />,
							applies:
								tab === 'vehicle' &&
								!!(wizardData.innerWizardData as VehicleBookingWizardData)?.trailerCheckboxSelected &&
								!(trip.vehicleBookingsClosed ?? false),
							hasSpace: hasSpaceLeft(trip.trailerSpotsAvailable, trip.trailerSpotsTotal),
						},
					},
				]),
		{
			order: appConfigStore.capacityOrderFreight,
			isDynamic: false,
			hasOrder: true,
			item: {
				element: <LegOptionFreightCapacity key="freight" />,
				applies: tab === 'freight',
				hasSpace: !(trip.freightBookingsClosed ?? false),
			},
		},
	];

	// Dynamic capacity types — same per-tab filtering as the previous LegOptionNestedCapacities.
	otherTags.forEach(capacityTag => {
		if (capacityTag.category === 'Vehicle' && wizardData.tabSelected !== 'vehicle') {
			return;
		}
		// A breakdown of a capacity that has nothing left — see `vehiclesSoldOut`.
		if (capacityTag.category === 'Vehicle' && vehiclesSoldOut) {
			return;
		}
		if (capacityTag.category === 'AddOn') {
			if (wizardData.tabSelected === 'vehicle' && !capacityTag.showForVehicleBookings) {
				return;
			}
			if (wizardData.tabSelected === 'passenger' && !capacityTag.showForPassengerBookings) {
				return;
			}
			if (wizardData.tabSelected === 'freight' && capacityTag.showForFreightBookings) {
				return;
			}
		}
		items.push({
			order: capacityTag.order ?? 0,
			isDynamic: true,
			hasOrder: capacityTag.order !== undefined && capacityTag.order !== null,
			item: {
				applies: true,
				hasSpace: hasSpaceLeft(capacityTag.remaining, capacityTag.total),
				element: (
				<SpacesRemainingTag
					key={capacityTag.id}
					space={capacityTag.remaining}
					capacityName={capacityTag.name}
					icon={<Icon name={capacityTag.icon} useMaterialIcons={capacityTag.useMaterialIcon} />}
					showCapacityRemaining={capacityTag.displayMode === 'ShowValue'}
					showName={showCapacityNames}
					explainClosed={explainClosedCapacity}
					explainState={explainCapacityState}
					tooltipText={showCapacityNames ? undefined : capacityTag.name}
					closed={isCapacityTagClosed(capacityTag, {
						vehicleBookingsClosed,
						passengerBookingsClosed,
						freightBookingsClosed,
					})}
				/>
				),
			},
		});
	});

	const chips = sortOrderedCapacities(items).filter(chip => chip.applies);

	if (collapseAfter === undefined || chips.length <= collapseAfter + 1) {
		return <>{chips.map(chip => chip.element)}</>;
	}

	// The visible places go to capacities with space, in display order; everything else — the
	// rest of those, and every empty one — is behind `+N more`, still in display order.
	const shown = chips.filter(chip => chip.hasSpace).slice(0, collapseAfter);
	const folded = chips.filter(chip => !shown.includes(chip));

	return (
		<>
			{shown.map(chip => chip.element)}
			<Popover
				floating
				openOnHover
				// The card behind this is itself a button — see `TripOptionNotes`.
				stopPropagation
				containerClassName="capacity-more"
				buttonClassName="capacity-more__trigger"
				popoverClassName="capacity-more__panel"
				buttonTestId="trip-option-more-capacities-trigger"
				buttonContent={
					<span
						className="spaces-remaining-tag__container capacity-more__chip"
						aria-label={intl.formatMessage(
							{
								id: 'trip-option__more-capacities__aria',
								defaultMessage: '{count} more capacities',
							},
							{ count: folded.length },
						)}
					>
						<FormattedMessage
							id="trip-option__more-capacities__label"
							defaultMessage="+{count} more"
							values={{ count: folded.length }}
						/>
					</span>
				}
				popoverContent={() => (
					<div
						// The chips are styled as `.trip-option` descendants, and the panel is portalled out of
						// the card — so it carries the card's classes to be drawn the same as the row it came from.
						className="trip-option capacity-more__list"
						data-test-id="trip-option-more-capacities-panel"
					>
						{folded.map(chip => chip.element)}
					</div>
				)}
			/>
		</>
	);
}
